import { useState, useEffect, useCallback } from 'react';

interface UseTypewriterOptions {
  texts: string[];
  typeSpeed?: number;
  deleteSpeed?: number;
  pauseBeforeDelete?: number;
  pauseBeforeType?: number;
}

export const useTypewriter = ({
  texts,
  typeSpeed = 100,
  deleteSpeed = 50,
  pauseBeforeDelete = 1500,
  pauseBeforeType = 500,
}: UseTypewriterOptions) => {
  const [currentTextIndex, setCurrentTextIndex] = useState(0);
  const [currentText, setCurrentText] = useState(texts[0]);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isTyping, setIsTyping] = useState(true);

  const type = useCallback(() => {
    const fullText = texts[currentTextIndex];
    
    if (isDeleting) {
      setCurrentText(prev => prev.slice(0, -1));
      
      if (currentText === '') {
        setIsDeleting(false);
        setCurrentTextIndex(prev => (prev + 1) % texts.length);
        setTimeout(() => setIsTyping(true), pauseBeforeType);
        return;
      }
    } else {
      setCurrentText(fullText.slice(0, currentText.length + 1));
      
      if (currentText === fullText) {
        setIsTyping(false);
        setTimeout(() => setIsDeleting(true), pauseBeforeDelete);
        return;
      }
    }
  }, [currentText, currentTextIndex, isDeleting, texts, pauseBeforeDelete, pauseBeforeType]);

  useEffect(() => {
    if (!isTyping && !isDeleting) return;

    const speed = isDeleting ? deleteSpeed : typeSpeed;
    const timer = setTimeout(type, speed);

    return () => clearTimeout(timer);
  }, [type, isDeleting, isTyping, typeSpeed, deleteSpeed]);

  return { currentText, isTyping: isTyping || isDeleting };
};